import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatNumber, formatCurrency } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui'
import { LineChart } from '@/components/charts'

type View = 'top' | 'bottom'
type StrategyGroupBy = 'main_strategy' | 'sub_strategy'

export default function WhatsWorking() {
  const [view, setView] = useState<View>('top')
  const [limit, setLimit] = useState(10)
  const [stratGroupBy, setStratGroupBy] = useState<StrategyGroupBy>('main_strategy')
  const [stratBest, setStratBest] = useState(true)

  // Load YTD rankings
  const { data: topFunds, isLoading: topLoading } = useQuery({
    queryKey: ['ytd-rankings', true, limit],
    queryFn: () => api.get(`/performance/ytd?limit=${limit}&best=true`).then((r) => r.data),
  })

  const { data: bottomFunds, isLoading: bottomLoading } = useQuery({
    queryKey: ['ytd-rankings', false, limit],
    queryFn: () => api.get(`/performance/ytd?limit=${limit}&best=false`).then((r) => r.data),
  })

  // Load by strategy (top/bottom, main/sub)
  const { data: byStrategy } = useQuery({
    queryKey: ['ytd-by-strategy', stratBest, limit, stratGroupBy],
    queryFn: () => api.get(`/performance/ytd/by-strategy?best=${stratBest}&count=${limit}&groupby=${stratGroupBy}`).then((r) => r.data),
  })

  // Load benchmarks
  const { data: benchmarks, isLoading: benchLoading } = useQuery({
    queryKey: ['hfri-benchmarks'],
    queryFn: () => api.get('/performance/benchmarks').then((r) => r.data),
  })

  // Load top performer NAV series
  const { data: topNav } = useQuery({
    queryKey: ['top-performer-nav', limit],
    queryFn: () => api.get(`/performance/ytd/top-nav?limit=${Math.min(limit, 10)}`).then((r) => r.data),
  })

  const activeFunds = view === 'top' ? topFunds : bottomFunds
  const isLoading = view === 'top' ? topLoading : bottomLoading

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">What's Working</h1>
          <p className="text-muted-foreground">YTD performance rankings and HFRI benchmarks</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Show:</label>
          <select
            className="h-9 px-3 border border-input rounded-md bg-background text-sm"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
          >
            <option value={10}>Top/Bottom 10</option>
            <option value={20}>Top/Bottom 20</option>
            <option value={50}>Top/Bottom 50</option>
          </select>
        </div>
      </div>

      {/* HFRI Benchmark Chart */}
      {benchmarks?.series?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">HFR Indices YTD</h2>
          {benchLoading ? (
            <LoadingSpinner />
          ) : (
            <BenchmarkChart series={benchmarks.series} />
          )}
        </div>
      )}

      {/* Top Performers NAV Chart */}
      {topNav?.series?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Top {Math.min(limit, 10)} Performers YTD</h2>
          <BenchmarkChart series={topNav.series} />
        </div>
      )}

      {/* Top / Bottom toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setView('top')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            view === 'top' ? 'bg-green-100 text-green-800 border border-green-300' : 'border border-input hover:bg-accent'
          }`}
        >
          Best Performers
        </button>
        <button
          onClick={() => setView('bottom')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            view === 'bottom' ? 'bg-red-100 text-red-800 border border-red-300' : 'border border-input hover:bg-accent'
          }`}
        >
          Worst Performers
        </button>
      </div>

      {/* Rankings table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {view === 'top' ? `Top ${limit} YTD Performers` : `Bottom ${limit} YTD Performers`}
          </h2>
        </div>
        {isLoading ? (
          <LoadingSpinner />
        ) : !activeFunds?.length ? (
          <div className="p-8 text-center text-muted-foreground">No data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fund</th>
                  <th>Firm</th>
                  <th>Strategy</th>
                  <th>Sub-Strategy</th>
                  <th className="text-right">AUM</th>
                  <th className="text-right">YTD Return</th>
                </tr>
              </thead>
              <tbody>
                {activeFunds.map((f: any, i: number) => (
                  <tr key={f.fund_id}>
                    <td className="text-muted-foreground">{i + 1}</td>
                    <td className="font-medium">{f.fund_name}</td>
                    <td className="text-muted-foreground">{f.firm}</td>
                    <td>{f.main_strategy}</td>
                    <td className="text-muted-foreground">{f.sub_strategy || '-'}</td>
                    <td className="text-right font-mono">{f.fund_assets ? formatCurrency(f.fund_assets, 0) : '-'}</td>
                    <td className={`text-right font-mono font-medium ${f.ytd_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(f.ytd_return, 1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* By Strategy */}
      {byStrategy && byStrategy.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold">
              {stratBest ? 'Top' : 'Worst'} Performers by {stratGroupBy === 'main_strategy' ? 'Strategy' : 'Sub-Strategy'}
            </h2>
            <div className="flex items-center gap-2">
              <div className="flex border border-input rounded-md overflow-hidden text-xs">
                <button
                  onClick={() => setStratBest(true)}
                  className={`px-3 py-1.5 ${stratBest ? 'bg-green-100 text-green-800' : 'hover:bg-accent'}`}
                >
                  Best
                </button>
                <button
                  onClick={() => setStratBest(false)}
                  className={`px-3 py-1.5 ${!stratBest ? 'bg-red-100 text-red-800' : 'hover:bg-accent'}`}
                >
                  Worst
                </button>
              </div>
              <div className="flex border border-input rounded-md overflow-hidden text-xs">
                <button
                  onClick={() => setStratGroupBy('main_strategy')}
                  className={`px-3 py-1.5 ${stratGroupBy === 'main_strategy' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                >
                  Main
                </button>
                <button
                  onClick={() => setStratGroupBy('sub_strategy')}
                  className={`px-3 py-1.5 ${stratGroupBy === 'sub_strategy' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                >
                  Sub
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th>Firm</th>
                  <th>Strategy</th>
                  <th>Sub-Strategy</th>
                  <th className="text-right">AUM</th>
                  <th className="text-right">YTD Return</th>
                </tr>
              </thead>
              <tbody>
                {byStrategy.map((f: any, i: number) => (
                  <tr key={`${f.fund_id}-${i}`}>
                    <td className="font-medium">{f.fund_name}</td>
                    <td className="text-muted-foreground">{f.firm}</td>
                    <td>{f.main_strategy}</td>
                    <td className="text-muted-foreground">{f.sub_strategy || '-'}</td>
                    <td className="text-right font-mono">{f.fund_assets ? formatCurrency(f.fund_assets, 0) : '-'}</td>
                    <td className={`text-right font-mono font-medium ${f.ytd_return >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(f.ytd_return, 1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Benchmark Chart ─── */

function BenchmarkChart({ series }: { series: { name: string; data: { date: string; nav: number }[] }[] }) {
  const dateMap = new Map<string, any>()

  for (const s of series) {
    for (const point of s.data) {
      if (!dateMap.has(point.date)) {
        dateMap.set(point.date, { date: point.date })
      }
      dateMap.get(point.date)![s.name] = point.nav
    }
  }

  const data = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date))
  const COLORS = [
    'hsl(var(--primary))',
    'hsl(210, 70%, 50%)',
    'hsl(150, 60%, 40%)',
    'hsl(30, 80%, 50%)',
    'hsl(280, 60%, 50%)',
    'hsl(0, 70%, 50%)',
    'hsl(180, 60%, 40%)',
    'hsl(60, 70%, 45%)',
  ]

  const lines = series.map((s, i) => ({
    key: s.name,
    name: s.name.replace('HFRI ', '').replace(' Index', ''),
    color: COLORS[i % COLORS.length],
  }))

  return (
    <LineChart
      data={data}
      xKey="date"
      lines={lines}
      height={350}
      yTickFormatter={(v: number) => formatNumber(v, 3)}
    />
  )
}
